import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { ensureUserGraphemeMeaningCard } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.create";
import { startOfNextLocalDay } from "@-/dev.improve.haniverse.lib.time.local-day";

export type DeferredGrapheme = {
  /** ISO instant the deferral ends: local midnight following `now`, as computed at the skip. */
  deferredUntil: string;
  grapheme: string;
  userCardId: string;
};

/**
 * "Skip until tomorrow" for a new-card lesson.
 *
 * Records a deferral WITHOUT enrolling: the `user_card` row is created (or reused) with no FSRS
 * card and no `startedStudyingAt`, exactly the non-study shape favorites and the permanent skip
 * use, so a deferred character never counts as studied, never becomes due, and never advances the
 * review-unit cadence. Only `deferredUntil` is written.
 *
 * The instant is computed HERE, at the moment of the skip, as the start of the next local calendar
 * day — not `now + 24h`. A daylight-saving transition makes that interval 23 or 25 hours, and the
 * persisted absolute timestamp is what lets a restarted app and a synced device agree on the
 * release moment. Repeating the skip later the same day recomputes the same boundary, so the write
 * is idempotent within a day.
 */
export async function deferGraphemeUntilNextLocalDay({
  actorDb,
  grapheme,
  now,
}: {
  actorDb: ActorDatabase;
  grapheme: string;
  now: Date;
}): Promise<DeferredGrapheme> {
  const deferredUntil = startOfNextLocalDay(now).toISOString();
  const userCard = await ensureUserGraphemeMeaningCard(actorDb, grapheme);
  await actorDb(import.meta.location)
    .update(actorSchema.userCard)
    .set({ deferredUntil, deletedAt: null })
    .where(eq(actorSchema.userCard.id, userCard.id))
    .run();
  return { deferredUntil, grapheme, userCardId: userCard.id };
}

/**
 * Whether a persisted `deferredUntil` still holds at `now`.
 *
 * A deferral is inclusive of its start and exclusive of its end: at exactly the stored instant the
 * card is eligible again. The ISO strings compare as instants because both are UTC-normalized by
 * the `date` column codec.
 */
export function isGraphemeDeferralActive(deferredUntil: string | null, now: Date): boolean {
  if (deferredUntil === null) {
    return false;
  }
  return new Date(deferredUntil).getTime() > now.getTime();
}
